import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import qs from 'qs'
import { useCallback } from 'react'

import type {
  FetchDevicePageOptions,
  FOTAJobCancelResponse,
  FOTAJobCompleteResponse,
  FotaJobPageFilters,
  JobRowData,
  PageOptions,
  PageResult,
} from '@/api/definition'
import { samFotaApiClient } from '@/api/index'
import useNonConcurrentRequest, { setPaginationParameters } from '@/api/Util'
import type { Device } from '@/pages/Device/Device'
import type {
  FotaBulkCreateView,
  FotaDeviceView,
  FOTAUpdateReadyJobListView,
} from '@/pages/fota/fota'

export interface PreFOTACheckResult {
  isOnline: boolean
  isInService: boolean
  isKeySwitchOperational: boolean
}

export interface FOTAJobRetryResponse {
  id: number
  successful: boolean
  checkPassed: boolean
  checkDetail: PreFOTACheckResult
  failureReason?: string
}

export const useFetchFotaDevicePage = function () {
  return {
    fetchFotaDevicePage: async function ({
      pageIndex,
      pageSize,
      sortBy,
      globalFilter,
      selectedFilters,
    }: FetchDevicePageOptions): Promise<PageResult<FotaDeviceView[]>> {
      let filters: string[][] = []
      for (const name in selectedFilters) {
        filters = [
          ...filters,
          ...Object.keys(selectedFilters[name]).map((key) => [name, key]),
        ]
      }
      const params = new URLSearchParams(filters)
      if (globalFilter) params.set('search', globalFilter)
      params.set('page', `${pageIndex + 1}`)
      params.set('per_page', `${pageSize}`)

      if (sortBy.length > 0) {
        const [{ id, desc }] = sortBy
        params.set('sort', id)
        params.set('sort_dir', desc ? 'desc' : 'asc')
      }
      const url = `available_updates?${params.toString()}`
      try {
        const requestConfig: AxiosRequestConfig = {
          url: url,
          method: 'GET',
        }
        const response =
          await samFotaApiClient.request<FotaDeviceView[]>(requestConfig)
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
  }
}

export async function postBulkUpdates(
  updates: number[],
): Promise<FotaBulkCreateView[]> {
  try {
    const response = await samFotaApiClient.post('jobs/bulk', { updates })
    return response.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    return []
  }
}

export const fetchPaginatedJobs = async (
  filters: FotaJobPageFilters,
): Promise<{ data: JobRowData[]; totalRows: number }> => {
  try {
    validateFilters(filters)
    const params = buildQueryParams(filters)
    const response = await fetchJobData(params)
    return processResponse(response)
  } catch (error) {
    return handleFetchError(error)
  }
}

const validateFilters = (filters: FotaJobPageFilters) => {
  if (filters.page < 0) throw new Error('Page number cannot be negative')
  if (filters.pageSize < 1) throw new Error('Page size must be at least 1')
  if (!filters.sortBy) throw new Error('Sort field is required')
}
type QueryParamValue = string | number | string[]
const buildQueryParams = (
  filters: FotaJobPageFilters,
): Record<string, QueryParamValue> => {
  const params: Record<string, QueryParamValue> = {
    page: filters.page + 1, // Convert to 1-based index for API
    per_page: filters.pageSize,
    sort: filters.sortBy,
    sort_dir: filters.sortDir ?? 'desc',
  }

  // Add optional parameters only if they have values
  if (filters.searchTerm?.trim()) {
    params.search = filters.searchTerm.trim()
  }

  if (filters.state?.length) {
    params.state = filters.state
  }
  return params
}

const fetchJobData = async (params: Record<string, QueryParamValue>) => {
  const requestConfig: AxiosRequestConfig = {
    url: 'jobs',
    method: 'GET',
    params,
    paramsSerializer: (params: Record<string, QueryParamValue>) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
    timeout: 10000,
  }

  return samFotaApiClient.request<JobRowData[]>(requestConfig)
}

const processResponse = (
  response: AxiosResponse<JobRowData[]>,
): { data: JobRowData[]; totalRows: number } => {
  if (!Array.isArray(response.data)) {
    throw new TypeError('Invalid response format: data is not an array')
  }

  // Parse total count with fallback
  const totalRows = response.headers['x-total-count']
    ? Number.parseInt(response.headers['x-total-count'], 10)
    : response.data.length

  // Transform and validate each row
  const transformedData = response.data.map((row) => {
    if (!row || typeof row !== 'object') {
      throw new Error('Invalid row data format')
    }
    return {
      ...row,
      id: row.id ?? row.uid, // Fallback to uid if id is missing
    }
  })

  return {
    data: transformedData,
    totalRows,
  }
}

const handleFetchError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Server responded with error status
      throw new Error(
        `API Error: ${error.response.status} - ${error.response.statusText}`,
      )
    } else if (error.request) {
      // Request made but no response
      throw new Error('No response received from server')
    } else {
      // Request setup error
      throw new Error(`Request Error: ${error.message}`)
    }
  } else if (error instanceof Error) {
    // Re-throw validation errors
    throw error
  } else {
    // Unknown error type
    throw new Error('An unexpected error occurred')
  }
}

export async function postBulkUpdatesForUpdateReady(
  updates: number[],
): Promise<FOTAUpdateReadyJobListView> {
  try {
    const response = await samFotaApiClient.post('jobs/update', {
      job_ids: updates,
    })
    return response.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    return {}
  }
}

export async function postCancelJobs(
  jobs: number[],
): Promise<FOTAJobCancelResponse[]> {
  try {
    const response = await samFotaApiClient.post('jobs/cancel', {
      job_ids: jobs,
    })
    return response.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    return []
  }
}

export async function postCompleteConfirmJobs(
  jobs: number[],
): Promise<FOTAJobCompleteResponse[]> {
  try {
    const response = await samFotaApiClient.post('jobs/confirm', {
      job_ids: jobs,
    })
    return response.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    return []
  }
}

export async function postBulkRetryJobs(
  jobs: number[],
): Promise<FOTAJobRetryResponse[]> {
  try {
    const response = await samFotaApiClient.post('jobs/retry', { jobs: jobs })
    return response.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    return []
  }
}

export type FotaDeviceHistory = {
  updatedTime: string
  updatedBy: string
  firmwareType: string
  previousFirmwareVersion: string
  currentFirmwareVersion: string
  status: string
}

export const useFetchFotaHistoryPage = function () {
  const ncr = useNonConcurrentRequest(samFotaApiClient)

  const fetchHistoryPage = useCallback(
    async function (
      uid: Device['uid'],
      { pageIndex, pageSize, sortBy, globalFilter }: PageOptions,
    ): Promise<PageResult<FotaDeviceHistory[]>> {
      const params = setPaginationParameters({
        pageIndex,
        pageSize,
        sortBy,
        globalFilter,
      })

      const url = `device/${uid}/history?${params.toString()}`
      try {
        const response = await ncr.request<FotaDeviceHistory[]>({
          url,
          method: 'GET',
        })
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
    [ncr],
  )

  return { fetchHistoryPage }
}
